import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Button, Paper, Stack, TextField, Typography, MenuItem,
  IconButton, Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import { obterFicha, salvarFicha, analisarFicha, notificarIndeferimento, liberarEdicao } from "../../services/fichaService";
import {
  getEdicoes, getSituacoesInscricao, getSituacoesEntrega, getTiposLancamento, getFormularioPorEdicao, getTiposDocumento,
} from "../../services/lookupService";
import { listarAnexos, baixarAnexo, avaliarAnexo, getAnexoBlob } from "../../services/anexoService";
import QuestionarioCampos from "../../components/QuestionarioCampos";
import LancamentosFixos, { montarLancamentos, valoresDeLancamentos } from "../../components/LancamentosFixos";
import CampoMoeda from "../../components/CampoMoeda";
import TabsBar from "../../components/TabsBar";
import DocumentosChecklist from "../../components/DocumentosChecklist";
import { agruparPorSecao, indexarPorCodigo } from "../../utils/questionario";

const novoFamiliar = () => ({ Nome: "", Parentesco: "", Idade: "", EstadoCivil: "", Escolaridade: "", Profissao: "", SalarioBruto: 0 });
const novoPatrimonio = () => ({ Tipo: "Imovel", Descricao: "", ValorMercado: 0, ValorParcela: 0, QtdParcelasAberto: "" });
const novoVeiculo = () => ({ Tipo: "Veiculo", Descricao: "", Ano: "" });
const novaSaude = () => ({ NomeCompleto: "", Parentesco: "", DoencaCronica: "", Deficiencia: "", EmTratamento: "" });

export default function FichaForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [aba, setAba] = useState(0);

  const [edicoes, setEdicoes] = useState([]);
  const [situacoes, setSituacoes] = useState([]);
  const [situacoesEntrega, setSituacoesEntrega] = useState([]);
  const [tiposR, setTiposR] = useState([]);
  const [tiposD, setTiposD] = useState([]);
  const [perguntas, setPerguntas] = useState([]);

  const [ficha, setFicha] = useState({ Id: 0, IdEdicao: "", IdSituacaoInscricao: "", IdInscrito: "" });
  const [responsavel, setResponsavel] = useState({ Nome: "", Cpf: "", Parentesco: "", RendaBruta: 0 });
  const [vinculoTipo, setVinculoTipo] = useState(""); // "RT" | "OUTRO"
  const [vinculoOutro, setVinculoOutro] = useState("");
  const [familia, setFamilia] = useState([]);
  const [patrimonio, setPatrimonio] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [saude, setSaude] = useState([]);
  const [valoresLanc, setValoresLanc] = useState({});
  const [respostas, setRespostas] = useState({});
  const [analise, setAnalise] = useState({ IdSituacaoEntrega: "", Parecer: "" });
  const [anexos, setAnexos] = useState([]);
  const [tiposDoc, setTiposDoc] = useState([]);

  const secoes = useMemo(() => agruparPorSecao(perguntas), [perguntas]);
  const byCodigo = useMemo(() => indexarPorCodigo(perguntas), [perguntas]);

  useEffect(() => {
    (async () => {
      const [ed, sit, ent, tr, td, tdoc] = await Promise.all([
        getEdicoes(), getSituacoesInscricao(), getSituacoesEntrega(), getTiposLancamento("R"), getTiposLancamento("D"), getTiposDocumento(),
      ]);
      setEdicoes(ed.data); setSituacoes(sit.data); setSituacoesEntrega(ent.data); setTiposR(tr.data); setTiposD(td.data); setTiposDoc(tdoc.data);
    })();
  }, []);

  const carregarFormulario = useCallback(async (idEdicao) => {
    if (!idEdicao) { setPerguntas([]); return; }
    setPerguntas((await getFormularioPorEdicao(idEdicao)).data);
  }, []);
  const carregarAnexos = useCallback(async (idFicha) => {
    if (!idFicha) return;
    setAnexos((await listarAnexos(idFicha)).data);
  }, []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data } = await obterFicha(id);
      setFicha(data.Ficha);
      setResponsavel(data.Responsavel || { Nome: "", Cpf: "", Parentesco: "", RendaBruta: 0 });
      const par = (data.Responsavel || {}).Parentesco || "";
      if (par === "Responsável/Tutor") { setVinculoTipo("RT"); setVinculoOutro(""); }
      else if (par) { setVinculoTipo("OUTRO"); setVinculoOutro(par); }
      setFamilia(data.ComposicaoFamiliar || []);
      const pats = data.Patrimonio || [];
      setPatrimonio(pats.filter((p) => p.Tipo !== "Veiculo"));
      setVeiculos(pats.filter((p) => p.Tipo === "Veiculo"));
      setSaude(data.SaudeFamiliar || []);
      setValoresLanc(valoresDeLancamentos(data.Lancamentos));
      setAnalise({ IdSituacaoEntrega: data.Ficha.IdSituacaoEntrega || "", Parecer: data.Ficha.ParecerAnalista || "" });
      const mapa = {};
      (data.Respostas || []).forEach((r) => { mapa[r.IdPergunta] = r.ValorBool ?? r.ValorNumero ?? r.ValorData ?? r.ValorTexto ?? r.IdOpcao ?? ""; });
      setRespostas(mapa);
      await carregarFormulario(data.Ficha.IdEdicao);
      await carregarAnexos(id);
    })();
  }, [id, carregarFormulario, carregarAnexos]);

  const onEdicao = (idEdicao) => { setFicha((f) => ({ ...f, IdEdicao: idEdicao })); carregarFormulario(idEdicao); };
  const setResp = (idPergunta, valor) => setRespostas((r) => ({ ...r, [idPergunta]: valor }));

  const montarRespostas = () =>
    perguntas.map((p) => {
      const v = respostas[p.Id];
      const r = { IdPergunta: p.Id, ValorBool: null, ValorNumero: null, ValorTexto: null, ValorData: null, IdOpcao: null };
      switch (p.TipoResposta) {
        case "bool": r.ValorBool = !!v; break;
        case "int": case "decimal": r.ValorNumero = v === "" || v == null ? null : Number(v); break;
        case "selecao": r.IdOpcao = v ? Number(v) : null; break;
        // Data tem coluna própria (ValorData) — é o que o servidor valida e o relatório lê.
        case "data": r.ValorData = v || null; break;
        default: r.ValorTexto = v ?? null;
      }
      return r;
    });

  const salvar = async () => {
    try {
      const dto = {
        Ficha: { ...ficha, IdEdicao: ficha.IdEdicao || null, IdSituacaoInscricao: ficha.IdSituacaoInscricao || null, IdInscrito: ficha.IdInscrito || null },
        Responsavel: { ...responsavel, Parentesco: vinculoTipo === "RT" ? "Responsável/Tutor" : vinculoTipo === "OUTRO" ? (vinculoOutro || "Outro") : null },
        ComposicaoFamiliar: familia,
        SaudeFamiliar: saude,
        Patrimonio: [...patrimonio, ...veiculos.map((v) => ({ ...v, Tipo: "Veiculo" }))]
          .map((p) => ({ ...p, QtdParcelasAberto: p.QtdParcelasAberto === "" ? null : Number(p.QtdParcelasAberto) })),
        Lancamentos: montarLancamentos(tiposR, tiposD, valoresLanc),
        Respostas: montarRespostas(),
      };
      const { data } = await salvarFicha(dto);
      await Swal.fire("Sucesso", data.mensagem, "success");
      navigate("/fichas");
    } catch (e) { Swal.fire("Erro", e?.response?.data || e.message, "error"); }
  };

  const registrarAnalise = async () => {
    try {
      await analisarFicha(id, { IdSituacaoInscricao: ficha.IdSituacaoInscricao || null, IdSituacaoEntrega: analise.IdSituacaoEntrega || null, Parecer: analise.Parecer });
      Swal.fire("Sucesso", "Análise registrada.", "success");
    } catch (e) { Swal.fire("Erro", e?.response?.data || e.message, "error"); }
  };

  const notificarIndeferido = async () => {
    const r = await Swal.fire({
      title: "Notificar indeferimento?",
      text: "Será enviado WhatsApp (e e-mail, se configurado) ao candidato sobre os documentos indeferidos.",
      icon: "question", showCancelButton: true, confirmButtonText: "Enviar notificação", cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
    try {
      const { data } = await notificarIndeferimento(id);
      Swal.fire("Enviado", `Notificação enviada (WhatsApp: ${data.whatsapp ? "ok" : "não"}, e-mail: ${data.email ? "ok" : "não"}).`, "success");
    } catch (e) {
      Swal.fire("Erro", e?.response?.data || e.message, "error");
    }
  };

  const liberarParaEdicao = async () => {
    const r = await Swal.fire({
      title: "Liberar para edição?",
      text: "A ficha voltará para 'Em preenchimento' e o candidato poderá editar e reenviar.",
      icon: "warning", showCancelButton: true, confirmButtonText: "Liberar", cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
    try {
      await liberarEdicao(id);
      setFicha((p) => ({ ...p, IdSituacaoInscricao: 1 }));
      Swal.fire("Liberada", "Ficha liberada para edição do candidato.", "success");
    } catch (e) { Swal.fire("Erro", e?.response?.data || e.message, "error"); }
  };

  const avaliarDoc = async (a, status, motivo, notificar) => {
    try {
      await avaliarAnexo(a.Id, status, motivo);
      await carregarAnexos(id);
      if (status !== "R") return;

      // Gravar e notificar são passos separados no backend: sem esta chamada o
      // candidato não recebe aviso nenhum (a mensagem antiga dizia que recebia).
      if (!notificar) {
        Swal.fire("Recusado", "Documento recusado. Use \"Notificar indeferimento\" para avisar o candidato.", "info");
        return;
      }
      try {
        const { data } = await notificarIndeferimento(id);
        Swal.fire("Recusado", data?.mensagem || "Documento recusado e candidato notificado.", "success");
      } catch (e2) {
        Swal.fire("Recusado, mas sem aviso",
          `O documento foi recusado, porém a notificação falhou: ${e2?.response?.data?.mensagem || e2?.response?.data || e2.message}`,
          "warning");
      }
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e?.response?.data || e.message, "error"); }
  };

  const updItem = (lista, set, i, campo, v) => set(lista.map((x, idx) => (idx === i ? { ...x, [campo]: v } : x)));
  const delItem = (lista, set, i) => set(lista.filter((_, idx) => idx !== i));

  // Helpers de seção do questionário
  const secaoLista = (nome) => secoes.find(([s]) => s === nome)?.[1] || [];
  const qTab = (nome) => {
    const lista = secaoLista(nome);
    if (!lista.length) return null;
    return { label: nome, node: <QuestionarioCampos perguntas={lista} respostas={respostas} setResp={setResp} byCodigo={byCodigo} /> };
  };
  const habitacional = secaoLista("Situação Habitacional");
  const perguntaVeic = byCodigo["hab_veiculos"];
  const possuiVeiculo = (() => {
    if (!perguntaVeic) return false;
    const op = (perguntaVeic.Opcoes || []).find((o) => String(o.Id) === String(respostas[perguntaVeic.Id]));
    return op?.Valor === "sim";
  })();

  const tabDadosGerais = {
    label: "Dados gerais", node: (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField select fullWidth label="Edição" value={ficha.IdEdicao || ""} onChange={(e) => onEdicao(e.target.value)}>
            {edicoes.map((e) => <MenuItem key={e.Id} value={e.Id}>{e.Nome}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField select fullWidth label="Situação" value={ficha.IdSituacaoInscricao || ""} onChange={(e) => setFicha({ ...ficha, IdSituacaoInscricao: e.target.value })}>
            {situacoes.map((s) => <MenuItem key={s.Id} value={s.Id}>{s.Descricao}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField fullWidth label="Nº do inscrito" value={ficha.IdInscrito || ""} onChange={(e) => setFicha({ ...ficha, IdInscrito: e.target.value })} />
        </Grid>
      </Grid>
    ),
  };

  const tabResponsavel = {
    label: "Responsável Financeiro", node: (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Nome" value={responsavel.Nome || ""} onChange={(e) => setResponsavel({ ...responsavel, Nome: e.target.value })} /></Grid>
        <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth label="CPF" value={responsavel.Cpf || ""} onChange={(e) => setResponsavel({ ...responsavel, Cpf: e.target.value })} /></Grid>
        <Grid size={{ xs: 6, sm: 3 }}><TextField fullWidth label="RG" value={responsavel.Rg || ""} onChange={(e) => setResponsavel({ ...responsavel, Rg: e.target.value })} /></Grid>
        <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth label="Tel. residencial" value={responsavel.Telefone || ""} onChange={(e) => setResponsavel({ ...responsavel, Telefone: e.target.value })} /></Grid>
        <Grid size={{ xs: 6, sm: 4 }}><TextField fullWidth label="Tel. celular" value={responsavel.Celular || ""} onChange={(e) => setResponsavel({ ...responsavel, Celular: e.target.value })} /></Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField select fullWidth label="Vínculo com o aluno" value={vinculoTipo} onChange={(e) => setVinculoTipo(e.target.value)}>
            <MenuItem value="RT">Responsável/Tutor</MenuItem>
            <MenuItem value="OUTRO">Outro</MenuItem>
          </TextField>
        </Grid>
        {vinculoTipo === "OUTRO" && (
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth label="Especificar vínculo" value={vinculoOutro} onChange={(e) => setVinculoOutro(e.target.value)} />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}><TextField fullWidth label="E-mail" value={responsavel.Email || ""} onChange={(e) => setResponsavel({ ...responsavel, Email: e.target.value })} /></Grid>
      </Grid>
    ),
  };

  const tabFamilia = {
    label: "Composição Familiar", node: (
      <>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setFamilia([...familia, novoFamiliar()])}>Adicionar</Button>
        {familia.map((m, i) => (
          <Stack key={i} direction="row" spacing={1} mt={1} useFlexGap flexWrap="wrap" alignItems="center">
            <TextField label="Nome" sx={{ flex: "1 1 160px" }} value={m.Nome || ""} onChange={(e) => updItem(familia, setFamilia, i, "Nome", e.target.value)} />
            <TextField label="Parentesco" sx={{ width: 130 }} value={m.Parentesco || ""} onChange={(e) => updItem(familia, setFamilia, i, "Parentesco", e.target.value)} />
            <TextField label="Idade" type="number" sx={{ width: 80 }} value={m.Idade || ""} onChange={(e) => updItem(familia, setFamilia, i, "Idade", e.target.value)} />
            <TextField label="Est. civil" sx={{ width: 120 }} value={m.EstadoCivil || ""} onChange={(e) => updItem(familia, setFamilia, i, "EstadoCivil", e.target.value)} />
            <TextField label="Escolaridade" sx={{ width: 150 }} value={m.Escolaridade || ""} onChange={(e) => updItem(familia, setFamilia, i, "Escolaridade", e.target.value)} />
            <TextField label="Profissão" sx={{ width: 150 }} value={m.Profissao || ""} onChange={(e) => updItem(familia, setFamilia, i, "Profissao", e.target.value)} />
            <CampoMoeda label="Salário" sx={{ width: 140 }} value={m.SalarioBruto} onChange={(n) => updItem(familia, setFamilia, i, "SalarioBruto", n)} />
            <IconButton color="error" onClick={() => delItem(familia, setFamilia, i)}><DeleteIcon /></IconButton>
          </Stack>
        ))}
      </>
    ),
  };

  const tabGrupo = {
    label: "Grupo Familiar", node: (
      <>
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Saúde do grupo familiar</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setSaude([...saude, novaSaude()])}>Adicionar</Button>
        {saude.map((s, i) => (
          <Stack key={i} direction="row" spacing={1} mt={1}>
            <TextField label="Nome" sx={{ flex: 2 }} value={s.NomeCompleto || ""} onChange={(e) => updItem(saude, setSaude, i, "NomeCompleto", e.target.value)} />
            <TextField label="Parentesco" sx={{ width: 130 }} value={s.Parentesco || ""} onChange={(e) => updItem(saude, setSaude, i, "Parentesco", e.target.value)} />
            <TextField label="Doença crônica" sx={{ flex: 1 }} value={s.DoencaCronica || ""} onChange={(e) => updItem(saude, setSaude, i, "DoencaCronica", e.target.value)} />
            <TextField label="Deficiência" sx={{ flex: 1 }} value={s.Deficiencia || ""} onChange={(e) => updItem(saude, setSaude, i, "Deficiencia", e.target.value)} />
            <IconButton color="error" onClick={() => delItem(saude, setSaude, i)}><DeleteIcon /></IconButton>
          </Stack>
        ))}
        {habitacional.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Situação habitacional</Typography>
            <QuestionarioCampos perguntas={habitacional} respostas={respostas} setResp={setResp} byCodigo={byCodigo} />
            {possuiVeiculo && (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Veículos (modelo e ano)</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setVeiculos([...veiculos, novoVeiculo()])}>Adicionar veículo</Button>
                </Stack>
                {veiculos.map((v, i) => (
                  <Stack key={i} direction="row" spacing={1} mt={1}>
                    <TextField label="Modelo" sx={{ flex: 2 }} value={v.Descricao || ""} onChange={(e) => updItem(veiculos, setVeiculos, i, "Descricao", e.target.value)} />
                    <TextField label="Ano" sx={{ width: 110 }} value={v.Ano || ""} onChange={(e) => updItem(veiculos, setVeiculos, i, "Ano", e.target.value)} />
                    <IconButton color="error" onClick={() => delItem(veiculos, setVeiculos, i)}><DeleteIcon /></IconButton>
                  </Stack>
                ))}
              </Box>
            )}
          </Box>
        )}
      </>
    ),
  };

  const tabReceitas = {
    label: "Receitas e Despesas",
    node: <LancamentosFixos tiposR={tiposR} tiposD={tiposD} valores={valoresLanc} setValores={setValoresLanc} />,
  };

  const tabBens = {
    label: "Bens Patrimoniais", node: (
      <>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setPatrimonio([...patrimonio, novoPatrimonio()])}>Adicionar</Button>
        {patrimonio.map((p, i) => (
          <Stack key={i} direction="row" spacing={1} mt={1}>
            <TextField select label="Tipo" sx={{ width: 120 }} value={p.Tipo || "Imovel"} onChange={(e) => updItem(patrimonio, setPatrimonio, i, "Tipo", e.target.value)}>
              <MenuItem value="Imovel">Imóvel</MenuItem><MenuItem value="Movel">Móvel</MenuItem>
            </TextField>
            <TextField label="Descrição" sx={{ flex: 2 }} value={p.Descricao || ""} onChange={(e) => updItem(patrimonio, setPatrimonio, i, "Descricao", e.target.value)} />
            <CampoMoeda label="Valor mercado" sx={{ width: 150 }} value={p.ValorMercado} onChange={(n) => updItem(patrimonio, setPatrimonio, i, "ValorMercado", n)} />
            <CampoMoeda label="Parcela" sx={{ width: 130 }} value={p.ValorParcela} onChange={(n) => updItem(patrimonio, setPatrimonio, i, "ValorParcela", n)} />
            <IconButton color="error" onClick={() => delItem(patrimonio, setPatrimonio, i)}><DeleteIcon /></IconButton>
          </Stack>
        ))}
      </>
    ),
  };

  // Ordem das abas espelhando o sistema antigo (Dados gerais e Análise são administrativos).
  const tabs = [
    tabDadosGerais,
    qTab("Instituição de Ensino de Origem"),
    qTab("Filiação"),
    tabResponsavel,
    tabFamilia,
    tabGrupo,
    tabReceitas,
    tabBens,
    qTab("Informações Complementares"),
  ].filter(Boolean);

  if (id) {
    tabs.push({
      label: "Documentos",
      node: (
        <DocumentosChecklist
          modo="analista" tipos={tiposDoc} anexos={anexos}
          onAvaliar={avaliarDoc} fetchBlob={getAnexoBlob}
          onBaixar={(a) => baixarAnexo(a.Id, a.NomeOriginal)} />
      ),
    });
    tabs.push({
      label: "Análise", node: (
        <>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField select fullWidth label="Situação da entrega de documentos" value={analise.IdSituacaoEntrega || ""} onChange={(e) => setAnalise({ ...analise, IdSituacaoEntrega: e.target.value })}>
                {situacoesEntrega.map((s) => <MenuItem key={s.Id} value={s.Id}>{s.Descricao}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField fullWidth multiline minRows={2} label="Parecer" value={analise.Parecer || ""} onChange={(e) => setAnalise({ ...analise, Parecer: e.target.value })} />
            </Grid>
          </Grid>
          {ficha.DtAnalise && (
            <Typography variant="caption" color="text.secondary">
              Última análise: {new Date(ficha.DtAnalise).toLocaleString("pt-BR")}{ficha.UsuarioAnalise ? ` por ${ficha.UsuarioAnalise}` : ""}
            </Typography>
          )}
          <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" color="secondary" onClick={registrarAnalise}>Registrar análise</Button>
            <Button variant="outlined" color="warning" disabled={!anexos.some((a) => a.Status === "R")} onClick={notificarIndeferido}>
              Notificar indeferimento (WhatsApp/e-mail)
            </Button>
            <Button variant="outlined" color="primary" disabled={ficha.IdSituacaoInscricao === 1} onClick={liberarParaEdicao}>
              Liberar para edição
            </Button>
          </Stack>
          {!anexos.some((a) => a.Status === "R") && (
            <Typography variant="caption" color="text.secondary">A notificação de indeferimento habilita quando houver ao menos um documento recusado.</Typography>
          )}
        </>
      ),
    });
  }

  const abaAtual = Math.min(aba, tabs.length - 1);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h5">{id ? `Ficha #${id}` : "Nova ficha"}</Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={() => navigate("/fichas")}>Voltar</Button>
          <Button variant="contained" onClick={salvar}>Salvar</Button>
        </Stack>
      </Stack>

      <Paper>
        <TabsBar tabs={tabs} value={abaAtual} onChange={setAba} />
        <Box sx={{ p: { xs: 2, sm: 3 } }}>{tabs[abaAtual].node}</Box>
      </Paper>
    </Box>
  );
}
